import { useEffect, useRef, useCallback, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X, GripHorizontal, RotateCcw } from "lucide-react";

interface BottomDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

export function BottomDrawer({ open, onClose, title, children }: BottomDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Free-drag state: position is an offset from the default anchored position
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragging = useRef(false);
  const startPointer = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Scroll drawer content to top & reset position when opened
  useEffect(() => {
    if (open) {
      setPosition(null);
      if (drawerRef.current) {
        const scrollArea = drawerRef.current.querySelector("[data-drawer-content]");
        if (scrollArea) scrollArea.scrollTop = 0;
      }
    }
  }, [open]);

  const getPointer = (e: TouchEvent | MouseEvent | React.TouchEvent | React.MouseEvent) => {
    if ("touches" in e) {
      const t = e.touches[0];
      return { x: t?.clientX ?? 0, y: t?.clientY ?? 0 };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const onDragStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    // Don't start drag if clicking the close button
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    dragging.current = true;
    const ptr = getPointer(e);
    startPointer.current = ptr;

    if (position) {
      // Continue from current free position
      startPos.current = { x: position.x, y: position.y };
    } else {
      // First drag — compute where the panel currently is and use that as origin
      // We'll track as offset from centered-bottom default
      startPos.current = { x: 0, y: 0 };
    }
    setIsDragging(true);
  }, [position]);

  const onDragMove = useCallback((e: TouchEvent | MouseEvent) => {
    if (!dragging.current) return;
    e.preventDefault();
    const ptr = getPointer(e);
    const dx = ptr.x - startPointer.current.x;
    const dy = ptr.y - startPointer.current.y;
    setPosition({
      x: startPos.current.x + dx,
      y: startPos.current.y + dy,
    });
  }, []);

  const onDragEnd = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    setIsDragging(false);
    // Position stays wherever user left it
  }, []);

  const resetPosition = useCallback(() => {
    setPosition(null);
  }, []);

  // Attach move/end listeners to window
  useEffect(() => {
    if (!open) return;
    const move = (e: TouchEvent | MouseEvent) => onDragMove(e);
    const end = () => onDragEnd();
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    return () => {
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
    };
  }, [open, onDragMove, onDragEnd]);

  const isDetached = position !== null;

  // When detached (freely positioned), render as a fixed floating panel
  // When anchored, render in the original bottom-sheet layout
  if (isDetached) {
    return createPortal(
      <div
        ref={drawerRef}
        className={`fixed z-[60] pointer-events-auto ${open ? "" : "hidden"}`}
        style={{
          left: `calc(50% + ${position.x}px)`,
          bottom: `calc(0px - ${position.y}px)`,
          transform: "translateX(-50%)",
          transition: isDragging ? "none" : undefined,
          width: "min(95vw, 72rem)",
        }}
      >
        <div
          ref={panelRef}
          className="bg-app-surface border border-app-border rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[70vh] pb-14 md:pb-0"
        >
          {/* Drag handle + Header */}
          <div
            className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-app-border shrink-0 cursor-grab active:cursor-grabbing select-none touch-none"
            onTouchStart={onDragStart}
            onMouseDown={onDragStart}
          >
            <GripHorizontal className="w-5 h-5 text-app-text-muted/40 shrink-0" />
            <h2 className="text-body font-semibold flex-1 pt-0.5">{title}</h2>
            <button
              onClick={resetPosition}
              title="Reset position"
              className="p-1.5 rounded-lg hover:bg-app-surface-raised text-app-text-muted hover:text-app-text transition-colors shrink-0"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-app-surface-raised text-app-text-muted hover:text-app-text transition-colors shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto overscroll-contain flex-1 p-4" data-drawer-content>
            {children}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // Default anchored bottom-sheet mode
  return createPortal(
    <div
      ref={drawerRef}
      className={`fixed bottom-0 left-0 right-0 z-[60] transition-transform duration-300 ease-out ${
        open ? "translate-y-0" : "translate-y-full"
      } pointer-events-none`}
    >
      {/* Match the main layout: skip sidebar space, apply same padding, same max-width */}
      <div className="md:pl-14 lg:pl-56 pointer-events-none">
        <div className="px-2 md:px-4 lg:px-6 pointer-events-none">
          <div className="w-full max-w-6xl mx-auto pointer-events-auto">
            <div
              ref={panelRef}
              className="bg-app-surface border border-app-border border-b-0 rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.15)] flex flex-col max-h-[70vh] mb-0 pb-14 md:pb-0"
            >
              {/* Drag handle + Header */}
              <div
                className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-app-border shrink-0 cursor-grab active:cursor-grabbing select-none touch-none"
                onTouchStart={onDragStart}
                onMouseDown={onDragStart}
              >
                <GripHorizontal className="w-5 h-5 text-app-text-muted/40 shrink-0" />
                <h2 className="text-body font-semibold flex-1 pt-0.5">{title}</h2>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-app-surface-raised text-app-text-muted hover:text-app-text transition-colors shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="overflow-y-auto overscroll-contain flex-1 p-4" data-drawer-content>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
